// ========================================
// 🚫 기존 포인트 서비스 (참고용 - 주석 처리됨)
// ========================================
// 새로운 포인트 시스템으로 완전 리팩토링됨
// 참고용으로만 보관

/*
/**
 * 포인트 관리 서비스
 * - 효율적인 포인트 처리 시스템
 * - 로컬 캐싱 및 실시간 업데이트 지원
 */

import pointsManager, { addAdRelatedPointsToQueue, recordAdSuccessResult } from './pointsManager';

// 포인트 소스 타입 정의
export const POINT_SOURCES = {
  SCHOLARSHIP: 'scholarship',
  TIMER: 'timer', 
  WELLNESS: 'wellness',
  REWARD: 'reward',
  EVENT: 'event',
  EXCHANGE: 'exchange',
  GENERAL: 'general'
};

// 포인트 설명 템플릿
export const POINT_DESCRIPTIONS = {
  [POINT_SOURCES.SCHOLARSHIP]: '장학금 수확',
  [POINT_SOURCES.TIMER]: '집중 타이머 완료',
  [POINT_SOURCES.WELLNESS]: '휴식 지원금',
  [POINT_SOURCES.REWARD]: '리워드 획득',
  [POINT_SOURCES.EVENT]: '이벤트 참여',
  [POINT_SOURCES.EXCHANGE]: '교환소 사용',
  [POINT_SOURCES.GENERAL]: '포인트 획득'
};

class PointsService {
  constructor() {
    this.localCache = {
      points: null,
      timestamp: null,
      cacheTimeout: 30000 // 30초 캐시
    };
  }

  /**
   * 포인트 차감 (교환소용) - 서버 직접 차감
   * @param {number} amount - 차감할 포인트
   * @param {string} description - 차감 설명
   */
  async subtractPoints(amount, description = '교환소 사용') {
    try {
      if (process.env.NODE_ENV === 'development') {
        // 포인트 차감 요청
      }
      
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('인증 토큰이 없습니다');
      }

      const response = await fetch('https://api.kkokk.io/api/points/subtract', {
        method: 'POST',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: amount,
          description: description
        })
      });

      if (!response.ok) {
        throw new Error(`서버 포인트 차감 실패: ${response.status}`);
      }

      const data = await response.json();
      if (process.env.NODE_ENV === 'development') {
        // 서버 포인트 차감 성공
      }
      
      // 서버 응답에서 포인트 정보 확인
      const newPoints = data.total_points || data.points || data.balance || 0;
      
      return {
        success: true,
        amount: amount,
        description: description,
        newPoints: newPoints
      };
    } catch (error) {
      console.error('❌ 포인트 차감 실패:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * 포인트 추가/차감 (벌크 큐에 추가) - 안전한 동기화
   * @param {number} amount - 추가할 포인트 (음수면 차감)
   * @param {string} source - 포인트 소스
   * @param {string} customDescription - 커스텀 설명 (선택사항)
   * @param {boolean} skipLocalUpdate - 로컬 캐시 업데이트 건너뛰기 (중복 업데이트 방지)
   */
  addPoints(amount, source = POINT_SOURCES.GENERAL, customDescription = null, skipLocalUpdate = false) {
    if (amount === 0) {
      console.warn('포인트는 0이 아닌 값이어야 합니다:', amount);
      return null;
    }

    const description = customDescription || POINT_DESCRIPTIONS[source] || POINT_DESCRIPTIONS[POINT_SOURCES.GENERAL];
    
    try {
      // 큐에 추가
      const entryId = pointsManager.addToQueue(amount, description, source);
      
      if (entryId) {
        // 로컬 캐시 즉시 업데이트 (옵티미스틱) - skipLocalUpdate가 false일 때만
        if (!skipLocalUpdate) {
          this.updateLocalCacheOptimistic(amount);
        }
        return entryId;
      } else {
        console.error('❌ 포인트 큐 추가 실패:', amount, description);
        return null;
      }
    } catch (error) {
      console.error('❌ 포인트 추가 중 오류:', error);
      return null;
    }
  }

  /**
   * 광고 관련 포인트 추가 (광고 성공 시에만 실제 처리)
   * @param {number} amount - 추가할 포인트
   * @param {string} source - 포인트 소스
   * @param {string} customDescription - 커스텀 설명 (선택사항)
   * @param {string} adRequestId - 광고 요청 ID
   */
  addAdRelatedPoints(amount, source = POINT_SOURCES.GENERAL, customDescription = null, adRequestId = null) {
    if (amount <= 0) {
      console.warn('포인트는 0보다 커야 합니다:', amount);
      return null;
    }

    const description = customDescription || POINT_DESCRIPTIONS[source] || POINT_DESCRIPTIONS[POINT_SOURCES.GENERAL];
    
    if (adRequestId) {
      // 광고 관련 포인트는 성공 확인 후 처리
      return addAdRelatedPointsToQueue(amount, description, source, adRequestId);
    } else {
      // 광고 ID가 없으면 일반 포인트로 처리
      return this.addPoints(amount, source, customDescription);
    }
  }

  /**
   * 광고 성공 결과 기록
   * @param {string} adRequestId - 광고 요청 ID
   * @param {boolean} success - 광고 성공 여부
   */
  recordAdSuccess(adRequestId, success) {
    recordAdSuccessResult(adRequestId, success);
    
    // 성공 시에만 옵티미스틱 업데이트 (실제 포인트는 큐에서 처리됨)
    if (success) {
      // 여기서는 옵티미스틱 업데이트를 하지 않음 (중복 방지)
      // 실제 포인트는 큐에 추가될 때 업데이트됨
    }
  }

  /**
   * 포인트 조회 (서버 우선 - 캐시 무시)
   */
  async getPoints() {
    try {
      const points = await this.fetchPointsFromServer();
      this.updateLocalCache(points);
      return points;
    } catch (error) {
      console.error('❌ 포인트 조회 실패:', error);
      throw error;
    }
  }

  /**
   * 서버에서 포인트 조회 - 강화된 에러 복구 로직 포함
   */
  async fetchPointsFromServer(retryCount = 0) {
    const maxRetries = 3;
    const retryDelay = 1000 * Math.pow(2, retryCount); // 지수 백오프: 1초, 2초, 4초
    
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('인증 토큰이 없습니다');
    }

    try {
      const response = await fetch('https://api.kkokk.io/api/points', {
        method: 'GET',
        headers: {
          'Authorization': token,
        },
      });

      // 500 에러인 경우 재시도
      if (response.status === 500 && retryCount < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, retryDelay));
        return this.fetchPointsFromServer(retryCount + 1);
      }
      
      // 401 에러 (인증 실패) - 재시도 불가
      if (response.status === 401) {
        console.error('🚨 인증 실패: 재로그인이 필요합니다.');
        throw new Error('인증 실패: 재로그인이 필요합니다.');
      }
      
      // 429 에러 (Rate Limit) - 지수 백오프로 재시도
      if (response.status === 429 && retryCount < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, retryDelay));
        return this.fetchPointsFromServer(retryCount + 1);
      }

      if (!response.ok) {
        throw new Error(`포인트 조회 실패: ${response.status}`);
      }

      const data = await response.json();
      const points = data.total_points || 0;
      
      return points;
      
    } catch (error) {
      // 네트워크 에러인 경우에도 재시도
      if (retryCount < maxRetries && (error.name === 'TypeError' || error.message.includes('fetch'))) {
        console.warn(`🔄 서버 포인트 조회 네트워크 에러, ${retryDelay}ms 후 재시도 (${retryCount + 1}/${maxRetries})`);
        await new Promise(resolve => setTimeout(resolve, retryDelay));
        return this.fetchPointsFromServer(retryCount + 1);
      }
      
      console.error(`❌ 서버 포인트 조회 최종 실패 (시도 ${retryCount + 1}/${maxRetries + 1}):`, error);
      throw error;
    }
  }

  /**
   * 로컬 캐시가 유효한지 확인 (서버 우선으로 항상 false 반환)
   */
  isLocalCacheValid() {
    // 🚀 서버 우선 정책으로 캐시 무효화
    return false;
  }

  /**
   * 로컬 캐시 업데이트
   */
  updateLocalCache(points) {
    this.localCache.points = points;
    this.localCache.timestamp = Date.now();
    
    // localStorage에도 저장
    localStorage.setItem('cached_points', points.toString());
    localStorage.setItem('cached_points_timestamp', this.localCache.timestamp.toString());
  }

  /**
   * 옵티미스틱 로컬 캐시 업데이트
   */
  updateLocalCacheOptimistic(amount) {
    // 캐시가 없으면 0으로 초기화
    if (this.localCache.points === null) {
      this.localCache.points = 0;
    }
    
    this.localCache.points += amount;
    localStorage.setItem('cached_points', this.localCache.points.toString());
    
    // 포인트 업데이트 이벤트 발생
    this.emitPointsUpdate();
  }

  /**
   * 포인트 업데이트 이벤트 발생
   */
  emitPointsUpdate() {
    const event = new CustomEvent('pointsUpdated', {
      detail: {
        points: this.localCache.points,
        timestamp: Date.now()
      }
    });
    window.dispatchEvent(event);
  }

  /**
   * localStorage에서 캐시 복원
   */
  restoreCacheFromStorage() {
    try {
      const cachedPoints = localStorage.getItem('cached_points');
      const cachedTimestamp = localStorage.getItem('cached_points_timestamp');
      
      if (cachedPoints && cachedTimestamp) {
        this.localCache.points = parseInt(cachedPoints, 10);
        this.localCache.timestamp = parseInt(cachedTimestamp, 10);
        
      }
    } catch (error) {
      console.warn('캐시 복원 실패:', error);
    }
  }

  /**
   * 캐시 초기화
   */
  clearCache() {
    this.localCache.points = null;
    this.localCache.timestamp = null;
    localStorage.removeItem('cached_points');
    localStorage.removeItem('cached_points_timestamp');
  }

  /**
   * 강제 동기화 (큐 플러시 + 포인트 새로고침 + 에그포인트 동기화) - 안전한 복구
   */
  async forceSync() {
    
    try {
      // 1. 큐에 있는 포인트들 즉시 처리
      await pointsManager.forceFlush();
      
      // 2. 서버에서 최신 포인트 조회 (캐시 무시)
      const points = await this.fetchPointsFromServer();
      this.updateLocalCache(points);
      
      // 3. 에그포인트 동기화
      await pointsManager.syncEggPoints();
      
      // 4. 포인트 복구 검증
      await this.validatePointsConsistency(points);
      
      return points;
    } catch (error) {
      console.error('❌ 포인트 강제 동기화 실패:', error);
      
      // 복구 시도
      try {
        const fallbackPoints = await this.fetchPointsFromServer();
        this.updateLocalCache(fallbackPoints);
        return fallbackPoints;
      } catch (fallbackError) {
        console.error('❌ 포인트 복구 실패:', fallbackError);
        throw error;
      }
    }
  }

  /**
   * 포인트 일관성 검증 - 강화된 안전장치
   */
  async validatePointsConsistency(serverPoints) {
    const localPoints = this.localCache.points;
    const difference = Math.abs(serverPoints - (localPoints || 0));
    
    if (difference > 10) { // 10포인트 이상 차이 시 경고 (더 민감하게)
      console.warn(`⚠️ 포인트 불일치 감지: 서버(${serverPoints}) vs 로컬(${localPoints}) - 차이: ${difference}`);
      
      // 포인트 백업 저장 (복구용)
      this.backupPoints(localPoints, serverPoints, difference);
      
      // 서버 포인트로 강제 동기화
      this.updateLocalCache(serverPoints);
      
      // 에러 이벤트 발생
      window.dispatchEvent(new CustomEvent('pointsInconsistency', {
        detail: { serverPoints, localPoints, difference }
      }));
    }
  }

  /**
   * 포인트 백업 저장 (복구용)
   */
  backupPoints(localPoints, serverPoints, difference) {
    const backup = {
      localPoints,
      serverPoints,
      difference,
      timestamp: Date.now(),
      userAgent: navigator.userAgent,
      url: window.location.href
    };
    
    // 최근 10개의 백업만 유지
    const backups = JSON.parse(localStorage.getItem('points_backups') || '[]');
    backups.push(backup);
    
    if (backups.length > 10) {
      backups.shift(); // 가장 오래된 백업 제거
    }
    
    localStorage.setItem('points_backups', JSON.stringify(backups));
  }

  /**
   * 포인트 백업 조회 (디버깅용)
   */
  getPointsBackups() {
    return JSON.parse(localStorage.getItem('points_backups') || '[]');
  }

  /**
   * 큐 상태 조회
   */
  getQueueStatus() {
    return pointsManager.getQueueStatus();
  }

  /**
   * 포인트 통계 조회
   */
  getPointsStats() {
    const queueStatus = this.getQueueStatus();
    
    return {
      cachedPoints: this.localCache.points,
      cacheTimestamp: this.localCache.timestamp,
      isCacheValid: this.isLocalCacheValid(),
      pendingPoints: queueStatus.totalPendingPoints,
      queueLength: queueStatus.queueLength,
      isProcessing: queueStatus.isProcessing,
      estimatedTotal: this.localCache.points ? 
        this.localCache.points + queueStatus.totalPendingPoints : null
    };
  }
}

// 싱글톤 인스턴스 생성
const pointsService = new PointsService();

// 앱 시작 시 캐시 복원
pointsService.restoreCacheFromStorage();

export default pointsService;

// 편의 함수들
export const addPoints = (amount, source, description) => {
  return pointsService.addPoints(amount, source, description);
};

export const addAdRelatedPoints = (amount, source, description, adRequestId) => {
  return pointsService.addAdRelatedPoints(amount, source, description, adRequestId);
};

export const recordAdSuccess = (adRequestId, success) => {
  return pointsService.recordAdSuccess(adRequestId, success);
};

export const getPoints = () => {
  return pointsService.getPoints();
};

export const syncPoints = () => {
  return pointsService.forceSync();
};

export const getPointsStats = () => {
  return pointsService.getPointsStats();
};

export const subtractPoints = (amount, description) => {
  return pointsService.subtractPoints(amount, description);
};
